import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { agencyService, AgencyProfile } from "../../services/agency.service";
import { AxiosError } from "axios";
import { clearLocalStorageExcept } from "@/utils/localStorageUtils";
import { destroyJivoChat } from "@/utils/jivoChat";

interface AgencyUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  code?: string;
  emailVerified: boolean;
  active: boolean;
  role: "AGENCY";
}

interface AgencyAuthContextType {
  user: AgencyUser | null;
  token: string | null;
  profile: AgencyProfile | null;
  login: (userData: any) => void;
  logout: () => void;
  updateProfile: (profile: AgencyProfile) => void;
  isLoading: boolean;
}

const AgencyAuthContext = createContext<AgencyAuthContextType | undefined>(
  undefined
);

interface AgencyAuthProviderProps {
  children: ReactNode;
}

export function AgencyAuthProvider({ children }: AgencyAuthProviderProps) {
  const [user, setUser] = useState<AgencyUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<AgencyProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("agency_token");
    const storedUser = localStorage.getItem("agency_user");

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);

        // Load full profile
        agencyService
          .getProfile()
          .then(setProfile)
          .catch((error) => {
            // Only logout on authentication errors, not network issues
            if (error instanceof AxiosError) {
              if (
                error.response?.status === 401 ||
                error.response?.status === 403
              ) {
                // Authentication/authorization failed - logout
                logout();
              } else {
                // Network error or other issues - just log error, don't logout
                console.warn(
                  "Failed to load agency profile (keeping user logged in):",
                  error.message
                );
              }
            } else {
              console.warn(
                "Failed to load agency profile (keeping user logged in):",
                error
              );
            }
          });
      } catch (error) {
        console.error("Failed to parse stored user data:", error);
        logout();
      }
    }
    setIsLoading(false);
  }, []);

  const login = (userData: any) => {
    const user: AgencyUser = {
      id: userData.id,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      code: userData.code,
      emailVerified: userData.emailVerified,
      active: userData.active,
      role: "AGENCY",
    };

    clearLocalStorageExcept("agency");
    setUser(user);
    setToken(userData.access_token);

    localStorage.setItem("agency_token", userData.access_token);
    localStorage.setItem("agency_user", JSON.stringify(user));

    // Load full profile after login
    agencyService
      .getProfile()
      .then(setProfile)
      .catch((error) => {
        // After login, profile loading failure shouldn't logout the user
        console.warn("Failed to load agency profile after login:", error);
      });
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setProfile(null);
    localStorage.removeItem("agency_token");
    localStorage.removeItem("agency_user");
    destroyJivoChat(); // Clean up JivoChat widget
  };

  const updateProfile = (updatedProfile: AgencyProfile) => {
    setProfile(updatedProfile);
  };

  return (
    <AgencyAuthContext.Provider
      value={{
        user,
        token,
        profile,
        login,
        logout,
        updateProfile,
        isLoading,
      }}
    >
      {children}
    </AgencyAuthContext.Provider>
  );
}

export function useAgencyAuth() {
  const context = useContext(AgencyAuthContext);
  if (context === undefined) {
    throw new Error("useAgencyAuth must be used within an AgencyAuthProvider");
  }
  return context;
}
